import { get, isEmpty } from "lodash";
import { ORGANIZATION_TYPES, USER_TYPES } from "utils/constants";
import { fill } from "utils/helpers/template";
import {
  EXTERNAL_STEPS,
  INTEGRATION_CATEGORIES,
  integrationOf,
  isExternal,
  STEP_KEYS,
} from "../Create/onboarding";

const INTERNAL_SECTIONS = [
  STEP_KEYS.GENERAL,
  STEP_KEYS.ADMIN,
  STEP_KEYS.KEY,
  STEP_KEYS.INTEGRATIONS,
  STEP_KEYS.API,
  STEP_KEYS.PLANS,
  STEP_KEYS.DOCUMENTS,
];

export const sectionsOf = (hosting) =>
  isExternal(hosting) ? EXTERNAL_STEPS : INTERNAL_SECTIONS;

export const SETUP_SECTIONS = [STEP_KEYS.PLANS, STEP_KEYS.DOCUMENTS];

export const ADMIN_TYPES = {
  [ORGANIZATION_TYPES.INS]: USER_TYPES.INSURANCE_ADMIN.type,
  [ORGANIZATION_TYPES.BROKER]: USER_TYPES.BROKER_ADMIN.type,
};

export const STATUS_TONES = {
  DONE: "done",
  TODO: "todo",
  LOCKED: "locked",
  DANGER: "danger",
  AFTER: "after",
};

export const ATTENTION_TONES = [STATUS_TONES.TODO, STATUS_TONES.DANGER];

export const LATER_FIELD_LABELS = { siretNumber: "SIRET", form: "FORM" };

export const API_CREDENTIAL_METHODS = {
  API_KEY: "API_KEY",
  CLIENT_SECRET: "CLIENT_SECRET",
  PRIVATE_KEY: "PRIVATE_KEY",
};

export const ENCRYPTION_MODES = {
  ORGANIZATION_ACCOUNT: "ORGANIZATION_ACCOUNT",
  IMPORTED: "IMPORTED",
};

export const ENCRYPTION_STATUSES = {
  NOT_CONFIGURED: "NOT_CONFIGURED",
  AWAITING_IMPORT: "AWAITING_IMPORT",
  ACTIVE: "ACTIVE",
  REVOKED: "REVOKED",
};

export const INVITATION_STATUSES = {
  PENDING: "PENDING",
  ACCEPTED: "ACCEPTED",
  UNAVAILABLE: "UNAVAILABLE",
};

export const SETUP_COUNTS = {
  PRODUCT_RANGES: "productRanges",
  INSURANCE_PLANS: "insurancePlans",
  PRICERS: "pricers",
  DISTRIBUTIONS: "distributions",
  EMAIL_TEMPLATES: "emailTemplates",
  REMINDERS: "reminders",
};

const PLAN_ITEMS = {
  [ORGANIZATION_TYPES.INS]: ["PRODUCT_RANGES", "INSURANCE_PLANS", "PRICERS"],
  [ORGANIZATION_TYPES.BROKER]: ["DISTRIBUTIONS"],
};

const DOCUMENT_ITEMS = ["EMAIL_TEMPLATES", "REMINDERS"];

export const setupItemsOf = (section, type) =>
  section === STEP_KEYS.PLANS ? PLAN_ITEMS[type] || [] : DOCUMENT_ITEMS;

const KEY_TONES = {
  [ENCRYPTION_STATUSES.ACTIVE]: STATUS_TONES.DONE,
  [ENCRYPTION_STATUSES.REVOKED]: STATUS_TONES.DANGER,
};

export const keyStatusOf = (encryption) =>
  encryption.status || ENCRYPTION_STATUSES.NOT_CONFIGURED;

const keyTodoOf = (status, labels) => {
  if (status === ENCRYPTION_STATUSES.ACTIVE) return [];
  const text = get(labels.TODO, status);
  return [
    status === ENCRYPTION_STATUSES.REVOKED
      ? { text, tone: STATUS_TONES.DANGER }
      : text,
  ];
};

const integrationTodoOf = ({ values, catalog, locked, labels, t }) => {
  if (locked) return [{ text: labels.LOCKED_TODO, tone: STATUS_TONES.LOCKED }];
  return INTEGRATION_CATEGORIES.map((category) =>
    integrationOf(values, catalog, category),
  )
    .filter(({ on, keys, filled }) => on && filled < keys.length)
    .map((integration) => ({
      text: t(`ONBOARDING.INTEGRATIONS.ITEMS.${integration.category}.TITLE`),
      detail: [
        get(integration, "connector.vendor") ||
          get(integration, "connector.label"),
        t("ONBOARDING.REMAINING.KEYS_COUNT", {
          filled: integration.filled,
          count: integration.keys.length,
        }),
      ],
    }));
};

const pendingOf = (access) =>
  (access.credentials || []).filter(({ pending }) => pending);

const apiStatusOf = ({ access, t }) => {
  if (!access.enabled)
    return { tone: STATUS_TONES.AFTER, text: t("DETAIL.API.STATUS.OFF") };
  if (isEmpty(access.credentials))
    return { tone: STATUS_TONES.TODO, text: t("DETAIL.API.STATUS.EMPTY") };
  const pending = pendingOf(access).length;
  if (pending > 0)
    return {
      tone: STATUS_TONES.TODO,
      text: t("DETAIL.API.STATUS.PENDING", { count: pending }),
    };
  return {
    tone: STATUS_TONES.DONE,
    text: t("DETAIL.API.STATUS.ON", { count: access.credentials.length }),
  };
};

const apiTodoOf = ({ access, labels }) => {
  if (!access.enabled) return [];
  if (isEmpty(access.credentials)) return [labels.TODO];
  return pendingOf(access).map(({ label }) =>
    fill(labels.TODO_PENDING, { label }),
  );
};

const missingSetupOf = ({ section, type, setup, labels }) =>
  setupItemsOf(section, type)
    .filter((item) => !get(setup, SETUP_COUNTS[item]))
    .map((item) => get(labels, [item, "TITLE"]));

export const environmentTodoOf = (ready, labels) =>
  ready ? [] : [labels.TODO];

export const laterFieldLabelsOf = (fields, labels) =>
  fields.map((field) => get(labels, LATER_FIELD_LABELS[field]));

export const setupTodosOf = ({
  values,
  catalog,
  keyStatus,
  access,
  setup,
  type,
  labels,
  t,
}) => {
  const ENCRYPTION = labels.ENCRYPTION;
  const setupTodo = (section) =>
    missingSetupOf({ section, type, setup, labels: labels.SETUP });
  return {
    [STEP_KEYS.KEY]: keyTodoOf(keyStatus, ENCRYPTION),
    [STEP_KEYS.INTEGRATIONS]: integrationTodoOf({
      values,
      catalog,
      locked: keyStatus !== ENCRYPTION_STATUSES.ACTIVE,
      labels: ENCRYPTION,
      t,
    }),
    [STEP_KEYS.API]: apiTodoOf({ access, labels: labels.API }),
    [STEP_KEYS.PLANS]: setupTodo(STEP_KEYS.PLANS),
    [STEP_KEYS.DOCUMENTS]: setupTodo(STEP_KEYS.DOCUMENTS),
  };
};

export const setupStatusOf = (key, { keyStatus, access, labels, t }) => {
  const ENCRYPTION = labels.ENCRYPTION;
  if (key === STEP_KEYS.KEY)
    return {
      tone: KEY_TONES[keyStatus] || STATUS_TONES.TODO,
      text: get(ENCRYPTION.STATUS, keyStatus),
    };
  if (
    key === STEP_KEYS.INTEGRATIONS &&
    keyStatus !== ENCRYPTION_STATUSES.ACTIVE
  )
    return { tone: STATUS_TONES.LOCKED, text: ENCRYPTION.LOCKED_STATUS };
  if (key === STEP_KEYS.API) return apiStatusOf({ access, t });
  return null;
};

export const todoStatusOf = (key, items, t) => ({
  tone: STATUS_TONES.TODO,
  text: t(
    SETUP_SECTIONS.includes(key)
      ? "DETAIL.STATUS.TODO_SETUP"
      : "DETAIL.STATUS.TODO",
    { count: items.length },
  ),
});
